import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AdminLayout from "@/components/AdminLayout";
import ErrorBoundary from "@/components/ErrorBoundary";
import ChannelsPanel from "@/components/ChannelsPanel";
import { adminApi } from "@/lib/api";
import { errText, monthLabel, monthShort } from "@/lib/ranges";

// Pass 59 — Marketing & attribution. Spend against return by channel,
// monthly spend entry, and attribution capture, over one window: the
// trailing 90 days (default; single months are noise at a handful of
// wins per channel) or a calendar month.

const ATTRIBUTION_LABELS = {
  gclid: "Google click ID (gclid)",
  fbclid: "Meta click ID (fbclid)",
  fbp: "Meta browser ID (fbp)",
  email: "Email",
  phone: "Phone",
  utm_source: "UTM source",
};

const RECENT_MONTH_BUTTONS = 4;

function StatBar({ label, percent, count, total }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 4 }}>
        <span>{label}</span>
        <span style={{ color: "var(--scw-muted)" }}>{count}/{total} · {percent}%</span>
      </div>
      <div style={{ height: 6, background: "#f0f0f0", borderRadius: 3, overflow: "hidden" }}>
        <div style={{ width: `${percent}%`, height: "100%", background: "#5C5C5C" }} />
      </div>
    </div>
  );
}

function WindowButton({ active, onClick, children, testId }) {
  return (
    <button type="button" onClick={onClick} data-testid={testId}
      className={active ? "scw-btn" : "scw-btn scw-btn-ghost"}
      style={{ padding: "6px 14px", fontSize: 12 }}
      aria-pressed={active}>
      {children}
    </button>
  );
}

function WindowPicker({ value, months, loading, onChange }) {
  const newest = (months || []).slice().reverse();
  const recent = newest.slice(0, RECENT_MONTH_BUTTONS);
  const older = newest.slice(RECENT_MONTH_BUTTONS);
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", marginBottom: 24 }} data-testid="marketing-window">
      <WindowButton active={value === "90d"} onClick={() => onChange("90d")} testId="marketing-window-90d">Last 90 days</WindowButton>
      {recent.map((ym) => (
        <WindowButton key={ym} active={value === ym} onClick={() => onChange(ym)} testId={`marketing-window-${ym}`}>
          {monthShort(ym)}
        </WindowButton>
      ))}
      {older.length > 0 && (
        <select className="scw-select" value={older.includes(value) ? value : ""}
          onChange={(e) => e.target.value && onChange(e.target.value)} data-testid="marketing-window-older"
          style={{ width: "auto" }}>
          <option value="">Earlier month</option>
          {older.map((ym) => <option key={ym} value={ym}>{monthLabel(ym)}</option>)}
        </select>
      )}
      {loading && <span style={{ fontSize: 12, color: "var(--scw-muted)" }}>{"Loading…"}</span>}
    </div>
  );
}

export default function AdminMarketing() {
  const [win, setWin] = useState("90d");
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (w) => {
    setErr("");
    setLoading(true);
    try {
      const r = await adminApi.analyticsMarketing({ window: w });
      setData(r.data);
      setWin(w); // only once it loaded, so the highlight matches the figures
    } catch (e) {
      setErr(errText(e, "Failed to load."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load("90d"); }, [load]);

  const onWindow = (w) => load(w);

  const header = (
    <>
      <Link to="/admin/analytics" style={{ fontSize: 12, color: "var(--scw-muted)", textDecoration: "none" }} data-testid="marketing-back">
        {"← Analytics"}
      </Link>
      <h1 style={{ fontSize: 28, margin: "10px 0 20px 0" }}>Marketing &amp; attribution</h1>
    </>
  );

  if (loading && !data) return <AdminLayout>{header}{"Loading…"}</AdminLayout>;
  if (err && !data) {
    return (
      <AdminLayout>
        {header}
        <div style={{ padding: 24, border: "1px solid var(--scw-line)", background: "#fcf8f4" }} data-testid="marketing-load-error">
          <p className="scw-eyebrow" style={{ marginBottom: 6, color: "#a13b3b" }}>Marketing data temporarily unavailable</p>
          <p style={{ margin: "0 0 12px 0", fontSize: 13 }}>{err}</p>
          <button type="button" className="scw-btn scw-btn-ghost" style={{ padding: "6px 14px" }} onClick={() => load(win)}>Retry</button>
        </div>
      </AdminLayout>
    );
  }

  const attr = data.attribution;

  return (
    <AdminLayout>
      {header}
      <WindowPicker value={win} months={data.available_months} loading={loading} onChange={onWindow} />
      {err && <p style={{ fontSize: 12, color: "#a13b3b", marginBottom: 16 }}>{err}</p>}

      <h2 style={{ fontSize: 18, margin: "8px 0 16px 0" }}>Spend against return, by channel</h2>
      <ErrorBoundary label="Channels">
        <ChannelsPanel channels={data.channels} onReload={() => load(win)} />
      </ErrorBoundary>

      <h2 style={{ fontSize: 18, margin: "32px 0 16px 0" }}>Attribution capture</h2>
      <div className="scw-card" style={{ padding: 24, marginBottom: 32 }} data-testid="marketing-attribution">
        {attr.window_size === 0 ? (
          <p style={{ color: "var(--scw-muted)", fontSize: 13 }}>No leads in this window to audit.</p>
        ) : (
          <>
            <p style={{ fontSize: 12, color: "var(--scw-muted)", marginBottom: 16 }}>
              {`Share of ${attr.window_size} lead${attr.window_size === 1 ? "" : "s"} from ${data.range.from} to ${data.range.to} carrying each identifier.`}
            </p>
            {Object.entries(ATTRIBUTION_LABELS).map(([key, label]) => (
              <StatBar key={key} label={label} percent={attr.percent[key]} count={attr.counts[key]} total={attr.window_size} />
            ))}
            <p style={{ fontSize: 11, color: "var(--scw-muted)", margin: "12px 0 0 0", lineHeight: 1.6 }}>
              A click ID is what ties a lead to an ad. Email and phone let Google and Meta match the lead when there is no click ID.
            </p>
          </>
        )}
      </div>
    </AdminLayout>
  );
}
